import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from "react";
import {
  BellRing, FileArchive, Palette, Printer, RefreshCw, RotateCcw, Save,
  ScanLine, ShieldCheck, SlidersHorizontal, TextCursorInput, Upload, Wrench
} from "lucide-react";
import { Button, ErrorBanner, Field, Input, LoadingState, PageHeader, Panel, Select, StatusBadge } from "../components/Ui";
import { useAuth } from "../context/AuthContext";
import {
  brandingAssetUrl, defaultDeveloperSettings, DEVELOPER_SETTINGS_EVENT, loadDeveloperSettings,
  type DeveloperSettings, type ExampleTextSettings, type PrinterSettings, type ScannerSettings
} from "../lib/developerSettings";
import { requireSupabase } from "../lib/supabase";
import type { WarehouseCode } from "../types";

type Section = "branding" | "texts" | "scanner" | "printer" | "documents" | "workflow" | "notifications";

const warehouseLabels: Record<WarehouseCode, string> = { OB: "Oberhausen", MZ: "Merzenich" };
const sections = [
  { id: "branding" as const, label: "Optik & Logo", description: "Name, Farben und Firmenlogo", icon: Palette },
  { id: "texts" as const, label: "Beispieltexte", description: "Platzhalter und Suchhinweise", icon: TextCursorInput },
  { id: "scanner" as const, label: "Scanner", description: "Je Lager ein Scannerprofil", icon: ScanLine },
  { id: "printer" as const, label: "Etikettendruck", description: "Drucker und Etikettenformat", icon: Printer },
  { id: "documents" as const, label: "Dokumente", description: "Größe, Aufbewahrung und Backup", icon: FileArchive },
  { id: "workflow" as const, label: "App-Ablauf", description: "Wareneingang und Abholung", icon: SlidersHorizontal },
  { id: "notifications" as const, label: "Meldungen", description: "Dispo und Ereignisse", icon: BellRing }
];

function Toggle({ checked, onChange, label, hint, disabled }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint: string;
  disabled?: boolean;
}) {
  return (
    <label className={`settings-toggle ${disabled ? "settings-toggle--disabled" : ""}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className="settings-toggle__control" aria-hidden="true" />
      <span><strong>{label}</strong><small>{hint}</small></span>
    </label>
  );
}

export function DeveloperPage() {
  const { cloudConfigured, profile } = useAuth();
  const [section, setSection] = useState<Section>("branding");
  const [settings, setSettings] = useState<DeveloperSettings>(defaultDeveloperSettings);
  const [savedSettings, setSavedSettings] = useState<DeveloperSettings>(defaultDeveloperSettings);
  const [dispoEmail, setDispoEmail] = useState("");
  const [savedDispoEmail, setSavedDispoEmail] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [loading, setLoading] = useState(cloudConfigured);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const canSave = cloudConfigured && profile?.role === "admin";

  const load = useCallback(async () => {
    if (!cloudConfigured) {
      setSettings(defaultDeveloperSettings);
      setSavedSettings(defaultDeveloperSettings);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [nextSettings, emailResult] = await Promise.all([
        loadDeveloperSettings(),
        requireSupabase().from("app_settings").select("value").eq("key", "dispo_email").maybeSingle()
      ]);
      if (emailResult.error) throw emailResult.error;
      const nextEmail = typeof emailResult.data?.value === "string" ? emailResult.data.value : "";
      setSettings(nextSettings);
      setSavedSettings(nextSettings);
      setDispoEmail(nextEmail);
      setSavedDispoEmail(nextEmail);
      setLogoFile(null);
      setLogoPreview("");
    } catch (exception) {
      setError(exception instanceof Error ? exception.message : "Die Entwicklereinstellungen konnten nicht geladen werden.");
    }
    setLoading(false);
  }, [cloudConfigured]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => () => { if (logoPreview.startsWith("blob:")) URL.revokeObjectURL(logoPreview); }, [logoPreview]);

  const currentLogoUrl = logoPreview || brandingAssetUrl(settings.branding.logoPath);
  const dirty = useMemo(() => JSON.stringify(settings) !== JSON.stringify(savedSettings)
    || dispoEmail !== savedDispoEmail || Boolean(logoFile), [settings, savedSettings, dispoEmail, savedDispoEmail, logoFile]);

  function patchBranding(patch: Partial<DeveloperSettings["branding"]>) {
    setSettings((current) => ({ ...current, branding: { ...current.branding, ...patch } }));
  }
  function patchScanner(warehouse: WarehouseCode, patch: Partial<ScannerSettings>) {
    setSettings((current) => ({
      ...current,
      scanners: { ...current.scanners, [warehouse]: { ...current.scanners[warehouse], ...patch } }
    }));
  }
  function patchPrinter(warehouse: WarehouseCode, patch: Partial<PrinterSettings>) {
    setSettings((current) => ({
      ...current,
      printers: { ...current.printers, [warehouse]: { ...current.printers[warehouse], ...patch } }
    }));
  }
  function patchDocuments(patch: Partial<DeveloperSettings["documents"]>) {
    setSettings((current) => ({ ...current, documents: { ...current.documents, ...patch } }));
  }
  function patchWorkflow(patch: Partial<DeveloperSettings["workflow"]>) {
    setSettings((current) => ({ ...current, workflow: { ...current.workflow, ...patch } }));
  }
  function patchNotifications(patch: Partial<DeveloperSettings["notifications"]>) {
    setSettings((current) => ({ ...current, notifications: { ...current.notifications, ...patch } }));
  }
  function patchSupport(patch: Partial<DeveloperSettings["support"]>) {
    setSettings((current) => ({ ...current, support: { ...current.support, ...patch } }));
  }
  function patchExampleTexts(patch: Partial<ExampleTextSettings>) {
    setSettings((current) => ({ ...current, exampleTexts: { ...current.exampleTexts, ...patch } }));
  }

  function selectLogo(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setError("Das Logo muss eine PNG-, JPG- oder WebP-Datei sein.");
      event.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Das Logo darf höchstens 2 MB groß sein.");
      event.target.value = "";
      return;
    }
    if (logoPreview.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setError("");
  }

  function removeLogo() {
    if (logoPreview.startsWith("blob:")) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview("");
    patchBranding({ logoPath: null });
  }

  function validate() {
    if (!settings.branding.companyName.trim() || !settings.branding.productName.trim()) return "Firmen- und Produktname dürfen nicht leer sein.";
    if (!/^#[0-9a-f]{6}$/i.test(settings.branding.primaryColor) || !/^#[0-9a-f]{6}$/i.test(settings.branding.darkColor)) return "Bitte gültige Branding-Farben auswählen.";
    if (settings.documents.maxFileSizeMb < 1 || settings.documents.maxFileSizeMb > 15) return "Das PDF-Limit muss zwischen 1 und 15 MB liegen.";
    if (settings.documents.retentionYears < 1 || settings.documents.retentionYears > 30) return "Die Aufbewahrung muss zwischen 1 und 30 Jahren liegen.";
    for (const code of ["OB", "MZ"] as WarehouseCode[]) {
      const scanner = settings.scanners[code];
      const printer = settings.printers[code];
      if (scanner.dpi < 100 || scanner.dpi > 600) return `Die Scannerauflösung für ${warehouseLabels[code]} muss zwischen 100 und 600 dpi liegen.`;
      if (printer.labelWidthMm < 20 || printer.labelWidthMm > 150 || printer.labelHeightMm < 20 || printer.labelHeightMm > 150) return `Das Etikettenformat für ${warehouseLabels[code]} ist ungültig.`;
    }
    return "";
  }

  async function save() {
    if (!canSave) return;
    const validationError = validate();
    if (validationError) return setError(validationError);
    setBusy(true);
    setError("");
    setSuccess("");
    const client = requireSupabase();
    const previousLogoPath = savedSettings.branding.logoPath;
    let uploadedLogoPath: string | null = null;
    let nextSettings = settings;
    try {
      if (logoFile) {
        const extension = logoFile.type === "image/png" ? "png" : logoFile.type === "image/webp" ? "webp" : "jpg";
        uploadedLogoPath = `branding/company-logo-${Date.now()}.${extension}`;
        const uploadResult = await client.storage.from("branding-assets").upload(uploadedLogoPath, logoFile, {
          contentType: logoFile.type,
          cacheControl: "3600",
          upsert: false
        });
        if (uploadResult.error) throw uploadResult.error;
        nextSettings = { ...settings, branding: { ...settings.branding, logoPath: uploadedLogoPath } };
      }

      const { error: rpcError } = await client.rpc("configure_developer_settings", {
        target_value: nextSettings,
        target_dispo_email: dispoEmail.trim() || null
      });
      if (rpcError) throw rpcError;

      if (previousLogoPath && previousLogoPath !== nextSettings.branding.logoPath) {
        await client.storage.from("branding-assets").remove([previousLogoPath]);
      }
      setSettings(nextSettings);
      setSavedSettings(nextSettings);
      setSavedDispoEmail(dispoEmail);
      setLogoFile(null);
      setLogoPreview("");
      setSuccess("Entwicklereinstellungen gespeichert. Branding und Web-Konfiguration wurden aktualisiert.");
      window.dispatchEvent(new CustomEvent(DEVELOPER_SETTINGS_EVENT));
    } catch (exception) {
      if (uploadedLogoPath) await client.storage.from("branding-assets").remove([uploadedLogoPath]);
      setError(exception instanceof Error ? exception.message : "Die Einstellungen konnten nicht gespeichert werden.");
    }
    setBusy(false);
  }

  function restoreDefaults() {
    if (!window.confirm("Alle Felder auf die sicheren K&S-Standardwerte zurücksetzen? Gespeichert wird erst mit „Änderungen speichern“.")) return;
    setSettings(defaultDeveloperSettings);
    setDispoEmail("");
    setLogoFile(null);
    setLogoPreview("");
    setSuccess("");
    setError("");
  }

  function renderBranding() {
    return (
      <div className="developer-content-grid">
        <Panel title="Firmenauftritt" description="Diese Werte werden in der Web-Admin-Navigation verwendet.">
          <div className="settings-form-grid">
            <Field label="Firmenname"><Input value={settings.branding.companyName} maxLength={40} onChange={(event) => patchBranding({ companyName: event.target.value })} /></Field>
            <Field label="Produktname"><Input value={settings.branding.productName} maxLength={50} onChange={(event) => patchBranding({ productName: event.target.value })} /></Field>
            <Field label="Untertitel"><Input value={settings.branding.adminSubtitle} maxLength={50} onChange={(event) => patchBranding({ adminSubtitle: event.target.value })} /></Field>
            <Field label="Umgebungskennzeichnung" hint="z. B. Testbetrieb oder Produktiv"><Input value={settings.support.environmentLabel} maxLength={30} onChange={(event) => patchSupport({ environmentLabel: event.target.value })} /></Field>
            <Field label="Hauptfarbe"><Input type="color" value={settings.branding.primaryColor} onChange={(event) => patchBranding({ primaryColor: event.target.value.toUpperCase() })} /></Field>
            <Field label="Dunkle Hauptfarbe"><Input type="color" value={settings.branding.darkColor} onChange={(event) => patchBranding({ darkColor: event.target.value.toUpperCase() })} /></Field>
          </div>
        </Panel>
        <Panel title="Firmenlogo" description="PNG, JPG oder WebP; höchstens 2 MB.">
          <div className="logo-settings">
            <div className="logo-preview" style={{ background: settings.branding.darkColor }}>
              {currentLogoUrl ? <img src={currentLogoUrl} alt="Aktuelles Firmenlogo" /> : <strong>{settings.branding.companyName || "K&S"}</strong>}
            </div>
            <div><strong>Logo für die Navigation</strong><p>Ohne Datei verwendet das System weiterhin die kompakte Textmarke.</p><div className="settings-button-row"><label className="button button--secondary"><Upload size={15} /> Logo auswählen<input className="visually-hidden" type="file" accept="image/png,image/jpeg,image/webp" onChange={selectLogo} /></label>{(settings.branding.logoPath || logoFile) && <Button variant="danger" onClick={removeLogo}>Logo entfernen</Button>}</div></div>
          </div>
        </Panel>
      </div>
    );
  }

  function renderScanners() {
    return <div className="hardware-settings-grid">{(["OB", "MZ"] as WarehouseCode[]).map((code) => {
      const scanner = settings.scanners[code];
      return <Panel key={code} title={`Scanner ${warehouseLabels[code]}`} description={`Eigenes Eingangsprofil für Lager ${code}`}>
        <div className="settings-form-grid settings-form-grid--single">
          <Toggle checked={scanner.enabled} onChange={(enabled) => patchScanner(code, { enabled })} label="Scanner verwenden" hint="Scanner-PDF als Wareneingangsquelle anbieten" />
          <Field label="Hersteller und Modell"><Input value={scanner.model} onChange={(event) => patchScanner(code, { model: event.target.value })} placeholder={settings.exampleTexts.scannerModel} /></Field>
          <Field label="Netzwerkadresse"><Input value={scanner.networkAddress} onChange={(event) => patchScanner(code, { networkAddress: event.target.value })} placeholder={settings.exampleTexts.scannerAddress} /></Field>
          <Field label="Eingangsordner"><Input value={scanner.sourceFolder} onChange={(event) => patchScanner(code, { sourceFolder: event.target.value })} placeholder={settings.exampleTexts.scannerFolder} /></Field>
          <div className="settings-form-grid">
            <Field label="Auflösung"><Select value={scanner.dpi} onChange={(event) => patchScanner(code, { dpi: Number(event.target.value) })}><option value={200}>200 dpi</option><option value={300}>300 dpi</option><option value={400}>400 dpi</option><option value={600}>600 dpi</option></Select></Field>
            <Field label="Farbmodus"><Select value={scanner.colorMode} onChange={(event) => patchScanner(code, { colorMode: event.target.value as ScannerSettings["colorMode"] })}><option value="color">Farbe</option><option value="grayscale">Graustufen</option><option value="black_white">Schwarzweiß</option></Select></Field>
          </div>
          <Toggle checked={scanner.duplex} onChange={(duplex) => patchScanner(code, { duplex })} label="Doppelseitig scannen" hint="Vorder- und Rückseiten in einer PDF übernehmen" />
        </div>
      </Panel>;
    })}</div>;
  }

  function renderPrinters() {
    return <div className="hardware-settings-grid">{(["OB", "MZ"] as WarehouseCode[]).map((code) => {
      const printer = settings.printers[code];
      return <Panel key={code} title={`Etikettendrucker ${warehouseLabels[code]}`} description={`Druckprofil für Lager ${code}`}>
        <div className="settings-form-grid settings-form-grid--single">
          <Toggle checked={printer.enabled} onChange={(enabled) => patchPrinter(code, { enabled })} label="Etikettendruck aktiv" hint="Druckfunktion auf dem Lagergerät anzeigen" />
          <Field label="Hersteller und Modell"><Input value={printer.model} onChange={(event) => patchPrinter(code, { model: event.target.value })} placeholder={settings.exampleTexts.printerModel} /></Field>
          <Field label="Verbindung"><Select value={printer.connection} onChange={(event) => patchPrinter(code, { connection: event.target.value as PrinterSettings["connection"] })}><option value="bluetooth">Bluetooth</option><option value="network">Netzwerk</option><option value="usb">USB</option></Select></Field>
          <Field label="Druckweg"><Select value={printer.integration} onChange={(event) => patchPrinter(code, { integration: event.target.value as PrinterSettings["integration"] })}><option value="print_master">Print Master</option><option value="android_system">Android-Druckdienst</option><option value="network_service">Netzwerk-Druckdienst</option></Select></Field>
          <div className="settings-form-grid">
            <Field label="Breite in mm"><Input type="number" min={20} max={150} value={printer.labelWidthMm} onChange={(event) => patchPrinter(code, { labelWidthMm: Number(event.target.value) })} /></Field>
            <Field label="Höhe in mm"><Input type="number" min={20} max={150} value={printer.labelHeightMm} onChange={(event) => patchPrinter(code, { labelHeightMm: Number(event.target.value) })} /></Field>
          </div>
          <div className="label-preview" style={{ aspectRatio: `${printer.labelWidthMm} / ${printer.labelHeightMm}` }}><strong>{printer.labelWidthMm} × {printer.labelHeightMm} mm</strong><span>{printer.model || "Etikettendrucker"}</span></div>
        </div>
      </Panel>;
    })}</div>;
  }

  function renderDocuments() {
    return <div className="developer-content-grid"><Panel title="Lieferscheine und Speicher" description="Grenzen und Aufbewahrung der privaten Dokumente."><div className="settings-form-grid">
      <Field label="Maximale Dateigröße" hint="Technisches Bucket-Limit: 15 MB"><Input type="number" min={1} max={15} value={settings.documents.maxFileSizeMb} onChange={(event) => patchDocuments({ maxFileSizeMb: Number(event.target.value) })} /></Field>
      <Field label="Aufbewahrung in Jahren" hint="Vor Produktivstart fachlich und rechtlich bestätigen"><Input type="number" min={1} max={30} value={settings.documents.retentionYears} onChange={(event) => patchDocuments({ retentionYears: Number(event.target.value) })} /></Field>
      <Field label="Backup-Ziel" hint="Keine Zugangsdaten eintragen"><Input value={settings.documents.backupTarget} onChange={(event) => patchDocuments({ backupTarget: event.target.value })} placeholder={settings.exampleTexts.backupTarget} /></Field>
      <Field label="Support-Kontakt"><Input type="email" value={settings.support.contactEmail} onChange={(event) => patchSupport({ contactEmail: event.target.value })} placeholder={settings.exampleTexts.email} /></Field>
    </div><div className="settings-toggle-list"><Toggle checked={settings.documents.backupEnabled} onChange={(backupEnabled) => patchDocuments({ backupEnabled })} label="Externes Backup vorgesehen" hint="Kennzeichnet die Konfiguration; Zugangsdaten bleiben serverseitig" /><Toggle checked={settings.documents.easyTecRequired} onChange={(easyTecRequired) => patchDocuments({ easyTecRequired })} label="EasyTec-Upload erforderlich" hint="Wareneingang bleibt bis zur manuellen Ablage als offen markiert" /></div></Panel><Panel title="Sicherheitsgrenze" description="Diese Werte gehören bewusst nicht in den Browser."><div className="security-settings-notice"><ShieldCheck size={28} /><div><strong>Keine Passwörter oder API-Schlüssel</strong><p>Supabase-Service-Schlüssel, Mailanbieter-, OCR-, S3- oder Scanner-Kennwörter werden ausschließlich als serverseitige Secrets eingerichtet und niemals in diesem Menü gespeichert.</p></div></div></Panel></div>;
  }

  function renderWorkflow() {
    return <Panel title="Verhalten der Lager-App" description="Zentrale Vorgaben für den täglichen Ablauf."><div className="settings-form-grid settings-form-grid--padded"><Field label="Standard beim Wareneingang"><Select value={settings.workflow.defaultIntake} onChange={(event) => patchWorkflow({ defaultIntake: event.target.value as DeveloperSettings["workflow"]["defaultIntake"] })}><option value="scanner">Scanner-PDF auswählen</option><option value="camera">Kamera</option><option value="manual">Manuelle Eingabe</option></Select></Field></div><div className="settings-toggle-grid"><Toggle checked={settings.workflow.scannerEnabled} onChange={(scannerEnabled) => patchWorkflow({ scannerEnabled })} label="Scanner-Eingang" hint="Mehrseitige PDF als Quelle anbieten" /><Toggle checked={settings.workflow.cameraEnabled} onChange={(cameraEnabled) => patchWorkflow({ cameraEnabled })} label="Kamera-Eingang" hint="Lieferschein am Lagergerät fotografieren" /><Toggle checked={settings.workflow.manualEnabled} onChange={(manualEnabled) => patchWorkflow({ manualEnabled })} label="Manuelle Erfassung" hint="Wareneingang ohne Scanner vorbereiten" /><Toggle checked={settings.workflow.pickupSignatureRequired} onChange={(pickupSignatureRequired) => patchWorkflow({ pickupSignatureRequired })} label="Unterschrift verpflichtend" hint="Abholung erst nach Monteur-Unterschrift abschließen" /><Toggle checked={settings.workflow.autoPrintLabels} onChange={(autoPrintLabels) => patchWorkflow({ autoPrintLabels })} label="Etiketten automatisch öffnen" hint="Nach Einlagerung direkt zum Druck wechseln" /><Toggle checked={settings.workflow.returnsEnabled} onChange={(returnsEnabled) => patchWorkflow({ returnsEnabled })} label="Retouren verwenden" hint="Retouren-Menü und Statuswechsel aktivieren" /></div></Panel>;
  }

  function renderNotifications() {
    return <div className="developer-content-grid"><Panel title="Dispo-E-Mail" description="Empfänger für betriebliche Lagerereignisse."><div className="settings-form-grid settings-form-grid--single"><Field label="E-Mail-Adresse"><Input type="email" value={dispoEmail} onChange={(event) => setDispoEmail(event.target.value)} placeholder={settings.exampleTexts.email} /></Field></div></Panel><Panel title="Ereignisse" description="Welche Vorgänge eine Benachrichtigung erzeugen sollen."><div className="settings-toggle-list"><Toggle checked={settings.notifications.goodsReceipt} onChange={(goodsReceipt) => patchNotifications({ goodsReceipt })} label="Wareneingang" hint="Nach bestätigter Einlagerung" /><Toggle checked={settings.notifications.pickup} onChange={(pickup) => patchNotifications({ pickup })} label="Abholung" hint="Nach unterschriebener Übergabe" /><Toggle checked={settings.notifications.returnRegistered} onChange={(returnRegistered) => patchNotifications({ returnRegistered })} label="Retoure angemeldet" hint="Wenn Material zur Rückgabe markiert wurde" /><Toggle checked={settings.notifications.dailySummary} onChange={(dailySummary) => patchNotifications({ dailySummary })} label="Tagesübersicht" hint="Spätere zusammengefasste Tagesmail vorbereiten" /></div></Panel></div>;
  }

  function renderExampleTexts() {
    const textInput = (key: keyof ExampleTextSettings) => (
      <Input value={settings.exampleTexts[key]} maxLength={160} onChange={(event) => patchExampleTexts({ [key]: event.target.value })} />
    );
    return <div className="developer-content-grid">
      <Panel title="Suchfelder" description="Hinweise in den Suchleisten des Web-Admins."><div className="settings-form-grid settings-form-grid--single">
        <Field label="Wareneingänge">{textInput("goodsReceiptsSearch")}</Field>
        <Field label="Lagerbestand">{textInput("inventorySearch")}</Field>
        <Field label="Retouren">{textInput("returnsSearch")}</Field>
        <Field label="Abholungen">{textInput("pickupsSearch")}</Field>
      </div></Panel>
      <Panel title="Formularbeispiele" description="Beispiele für Aufträge, Lagerplätze und Stammdaten."><div className="settings-form-grid">
        <Field label="Auftragsnummer">{textInput("orderNumber")}</Field>
        <Field label="Lagerplatz">{textInput("storageLocation")}</Field>
        <Field label="Lieferant">{textInput("supplier")}</Field>
        <Field label="Optionales Feld">{textInput("optional")}</Field>
        <Field label="Retourengrund">{textInput("returnReason")}</Field>
        <Field label="Abteilungscode">{textInput("departmentCode")}</Field>
        <Field label="Abteilungsname">{textInput("departmentName")}</Field>
      </div></Panel>
      <Panel title="Geräte und Kontakt" description="Beispiele in Scanner-, Drucker- und Kontaktfeldern."><div className="settings-form-grid">
        <Field label="Scannermodell">{textInput("scannerModel")}</Field>
        <Field label="Scanneradresse">{textInput("scannerAddress")}</Field>
        <Field label="Scannerordner">{textInput("scannerFolder")}</Field>
        <Field label="Gerätename">{textInput("deviceName")}</Field>
        <Field label="Druckermodell">{textInput("printerModel")}</Field>
        <Field label="E-Mail-Adresse">{textInput("email")}</Field>
        <Field label="Backup-Ziel">{textInput("backupTarget")}</Field>
      </div></Panel>
    </div>;
  }

  function renderSection() {
    if (section === "branding") return renderBranding();
    if (section === "texts") return renderExampleTexts();
    if (section === "scanner") return renderScanners();
    if (section === "printer") return renderPrinters();
    if (section === "documents") return renderDocuments();
    if (section === "workflow") return renderWorkflow();
    return renderNotifications();
  }

  return (
    <>
      <PageHeader title="Entwickler" description="Zentrale technische und optische Konfiguration des K&S Lagersystems." action={<div className="page-header__actions"><Button variant="secondary" onClick={() => void load()} disabled={loading || busy}><RefreshCw size={16} /> Neu laden</Button><Button onClick={() => void save()} disabled={!canSave || busy || !dirty}><Save size={16} />{busy ? "Wird gespeichert …" : "Änderungen speichern"}</Button></div>} />
      {!cloudConfigured && <div className="notice-banner">Lokale Vorschau: Einstellungen werden angezeigt, aber erst mit verbundener Testcloud gespeichert.</div>}
      {error && <ErrorBanner message={error} />}
      {success && <div className="success-banner"><Wrench size={17} /><span>{success}</span></div>}
      <div className="developer-toolbar"><StatusBadge tone={dirty ? "warning" : "success"}>{dirty ? "Nicht gespeicherte Änderungen" : "Konfiguration gespeichert"}</StatusBadge><Button variant="secondary" className="button--small" onClick={restoreDefaults} disabled={busy}><RotateCcw size={14} /> Standardwerte laden</Button></div>
      <section className="developer-section-grid">{sections.map((item) => <button type="button" key={item.id} className={`developer-section-card ${section === item.id ? "developer-section-card--active" : ""}`} onClick={() => setSection(item.id)}><span><item.icon size={20} /></span><div><strong>{item.label}</strong><small>{item.description}</small></div></button>)}</section>
      {loading ? <LoadingState label="Entwicklereinstellungen werden geladen …" /> : renderSection()}
    </>
  );
}
